import { styled } from '@abyss/web/tools/styled';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { PrintViewDivider } from './PrintView.style';
import { getAddress, getFacilitySpecialities, getHoursOfOperation, getPhoneNumbers } from './printViewUtils';

const baseBoldTextStyle = {
  fontWeight: '700',
  color: '#212223',
};

export const PrintViewBoldText = styled('span', {
  ...baseBoldTextStyle,
});

export const PrintViewBoldTextWithMarginRight = styled('span', {
  ...baseBoldTextStyle,
  marginRight: '2px',
});

export const PrintViewNormalText = styled('span', {
  fontWeight: 'normal'
});

const BehavioralHealthFacilityPrintView = ({ index, portalData, provider }) => {
  const { t } = useTranslation();
  const notAvailable = t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN');

  const acceptingNewPatients = provider?.acceptingNewPatients;
  const accessibilities = provider?.accessibility?.join(', ') || notAvailable;
  const address = getAddress(provider?.address) || notAvailable;
  const afterHoursPhone =
    getPhoneNumbers(provider?.phones?.emergency) || notAvailable;
  const distance = provider?.distance
    ? `${parseFloat(provider?.distance).toFixed(1)} miles`
    : notAvailable;
  const email = provider?.emailAddress?.join(', ') || notAvailable;
  const faxPhone =
    getPhoneNumbers(provider?.phones?.fax) || notAvailable;
  const hasEveningAppointments = provider?.hasEveningAppointments == null ? notAvailable :
    (provider?.hasEveningAppointments ? 'Yes' : 'No');
  const hasWeekendAppointments = provider?.hasWeekendAppointment == null ? notAvailable :
    (provider?.hasWeekendAppointment ? 'Yes' : 'No');
  const hospitalAccreditation = provider?.hasJCAHOAccreditation
    ? 'The Joint Commission'
    : notAvailable;
  const hoursOfOperation = getHoursOfOperation(provider?.hoursOfOperation, t);
  const languagesSpokenByInterpreters =
    provider?.languagesSpokenByInterpreters?.join(', ') || notAvailable;
  const languagesSpokenByOffice =
    provider?.languagesSpokenByOffice?.join(', ') || notAvailable;
  const nextworkOrPlanName = portalData?.planName || notAvailable;
  const npiNumber = provider?.npi || notAvailable;
  const practiceAgeLimitations =
    provider?.practiceLimitations?.age || notAvailable;
  const practiceGenderLimitations =
    provider?.practiceLimitations?.gender || notAvailable;
  const providerName = provider?.providerName || notAvailable;
  const specialties = getFacilitySpecialities(provider?.facilityProgramIds, t)|| notAvailable;
  const telephone =
    getPhoneNumbers(provider?.phones?.phone) || notAvailable;
  const tty = provider?.tty || notAvailable;
  const virtualCareOffered = provider?.virtualCareOffered?.length > 0;
  const website = provider?.website || notAvailable;

  return (
    <React.Fragment>
      <Flex
        css={{
          'abyss-flex-root': {
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="column"
      >
        <Flex justify="flex-start">
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '6px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText>
              {index + 1}. {providerName}
            </PrintViewBoldText>
            <span>{address}</span>
          </Flex>
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '6px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText> {t('SHARE_PDF.Distance')} </PrintViewBoldText>
            <span> {distance} </span>
          </Flex>

          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '6px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText> Web </PrintViewBoldText>
            <span>{website}</span>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        css={{
          'abyss-flex-root': {
            fontSize: '10px',
            width: '100%',
            marginTop: '5px',
          },
        }}
        direction="row"
        justify="space-between"
      >
        <Flex
          css={{
            'abyss-flex-root': {
              width: '33%',
            },
          }}
          direction="column"
        >
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              Tel.: <PrintViewNormalText>{telephone}</PrintViewNormalText>
            </PrintViewBoldTextWithMarginRight>
          </Flex>
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.After hours')}: <PrintViewNormalText>{afterHoursPhone}</PrintViewNormalText>
            </PrintViewBoldTextWithMarginRight>
          </Flex>
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              TTY:
            </PrintViewBoldTextWithMarginRight>
            <span>{tty}</span>
          </Flex>
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Fax')}: <PrintViewNormalText>{faxPhone}</PrintViewNormalText>
            </PrintViewBoldTextWithMarginRight>
          </Flex>
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('FEEDBACK_FORM.EMAIL')}: <PrintViewNormalText>{email}</PrintViewNormalText>
            </PrintViewBoldTextWithMarginRight>
          </Flex>
        </Flex>
        <Flex
          css={{
            'abyss-flex-root': {
              width: '33%',
            },
          }}
          direction="column"
        >
          <PrintViewBoldTextWithMarginRight>
            {t('SHARE_PDF.Office hours')}:
          </PrintViewBoldTextWithMarginRight>
          {hoursOfOperation?.map((item, index) => (
            <Flex direction="row" key={index}>
              <PrintViewBoldTextWithMarginRight>
                {item.day}:
              </PrintViewBoldTextWithMarginRight>
              <span>{item.timing}</span>
            </Flex>
          ))}
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Evening appointments')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{hasEveningAppointments}</span>
          </Flex>
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Weekend appointments')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{hasWeekendAppointments}</span>
          </Flex>
        </Flex>
        <Flex
          css={{
            'abyss-flex-root': {
              width: '33%',
            },
          }}
          direction="column"
        >
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Practice age limitations')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{practiceAgeLimitations}</span>
          </Flex>
          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Practice gender limitations')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{practiceGenderLimitations}</span>
          </Flex>

          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Hospital accreditation')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{hospitalAccreditation}</span>
          </Flex>

          <Flex direction="row">
            <PrintViewBoldTextWithMarginRight>
              {t('SHARE_PDF.Network/Plan name')}:
            </PrintViewBoldTextWithMarginRight>
            <span>{nextworkOrPlanName}</span>
          </Flex>
        </Flex>
      </Flex>

      <div style={{ fontSize: '10px', marginTop: '5px' }}>
        <PrintViewBoldTextWithMarginRight>
          {t('PROVIDER_LOCATIONS_DETAILS.SPECIALTIES')}: <PrintViewNormalText>{specialties}</PrintViewNormalText>
        </PrintViewBoldTextWithMarginRight>
      </div>

      <Flex
        css={{
          'abyss-flex-root': {
            fontSize: '10px',
            marginTop: '5px',
          },
        }}
        direction="column"
      >
        <Flex direction="row">
          <PrintViewBoldTextWithMarginRight>
            {t('PROVIDER_LOCATIONS_DETAILS.ACCESSIBILITY')}: <PrintViewNormalText>{accessibilities}</PrintViewNormalText>
          </PrintViewBoldTextWithMarginRight>
        </Flex>
        <Flex direction="row">
          <PrintViewBoldTextWithMarginRight>
            {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_OFFICE')}: <PrintViewNormalText>{languagesSpokenByOffice}</PrintViewNormalText>
          </PrintViewBoldTextWithMarginRight>
        </Flex>
        <Flex direction="row">
          <PrintViewBoldTextWithMarginRight>
            {t('PROVIDER_LOCATIONS_DETAILS.LANGUAGES_SPOKEN_BY_INTERPRETER')}: <PrintViewNormalText>{languagesSpokenByInterpreters}</PrintViewNormalText>
          </PrintViewBoldTextWithMarginRight>
        </Flex>
      </Flex>
      <Flex
        css={{
          'abyss-flex-root': {
            marginTop: '5px',
            gap: '2px',
            padding: 0,
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '10px',
          },
        }}
        direction="column"
      >
        <Flex justify="flex-start">
          {acceptingNewPatients && (
            <Flex
              css={{
                'abyss-flex-root': {
                  marginRight: '8px',
                  gap: '4px',
                },
              }}
              direction="row"
            >
              <PrintViewBoldText>
                <IconMaterial
                  color="#111"
                  css={{
                    width: '12px',
                    height: '12px',
                    marginRight: '0px',
                    marginBottom: '-2px',
                  }}
                  icon="check"
                />{' '}
                {t('SHARE_PDF.Accepting new patients')}
              </PrintViewBoldText>
            </Flex>
          )}
          {virtualCareOffered && (
            <Flex
              css={{
                'abyss-flex-root': {
                  marginRight: '5px',
                  gap: '4px',
                },
              }}
              direction="row"
            >
              <PrintViewBoldText>
                <IconMaterial
                  color="#111"
                  css={{
                    width: '12px',
                    height: '12px',
                    marginRight: '0px',
                    marginBottom: '-2px',
                  }}
                  icon="videocam"
                  variant="filled"
                />
                {t('SHARE_PDF.Virtual care available')}
              </PrintViewBoldText>
            </Flex>
          )}
          <Flex
            css={{
              'abyss-flex-root': {
                marginRight: '5px',
                gap: '4px',
              },
            }}
            direction="row"
          >
            <PrintViewBoldText>
              {' '}
              {t('SHARE_PDF.National provider No. (NPI)')}:
            </PrintViewBoldText>
            <span>{npiNumber}</span>
          </Flex>
        </Flex>
      </Flex>
      <PrintViewDivider orientation="horizontal" />
    </React.Fragment>
  );
};

export default BehavioralHealthFacilityPrintView;
